import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  ArrowDown,
  Award,
  CheckCircle,
  Clock,
  Smartphone,
  Globe,
  Brain,
  Target,
  DollarSign,
  BarChart3,
  Heart,
  Lightbulb,
  ArrowRight,
  User,
  Users,
  MessageCircle,
  Calendar
} from 'lucide-react';

// WhatsApp Icon SVG Component
const WhatsAppIcon = ({ className = "" }) => (
  <svg className={className} width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

// Navigation Component - Neo-Brutalist Clean
const Navigation = () => (
  <nav className="sticky top-0 z-50 bg-cream border-b-4 border-text-dark" data-testid="academy-navigation">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex justify-between items-center py-4">
        <Link to="/" className="flex items-center" data-testid="logo-link">
          <span className="font-poppins font-black text-2xl md:text-3xl tracking-tight text-text-dark">
            BOOST<span className="text-pink">CHANGE</span>
          </span>
        </Link>
        <div className="flex items-center gap-6">
          <Link to="/" className="hidden md:flex items-center gap-2 font-poppins font-semibold text-text-dark hover:text-pink transition-colors">
            <ArrowLeft className="w-4 h-4" /> Volver al inicio
          </Link>
          <a
            href="#cursos"
            className="bg-yellow text-text-dark font-poppins font-bold uppercase tracking-widest px-6 py-3 border-4 border-text-dark hover:translate-y-1 transition-all"
            style={{ boxShadow: '5px 5px 0px #000', borderRadius: '0px' }}
            data-testid="academy-cta"
          >
            Ver Cursos
          </a>
        </div>
      </div>
    </div>
  </nav>
);

// Hero Section - UPDATED: Cream background, yellow buttons as accent
const HeroSection = () => (
  <section className="bg-cream py-16 md:py-24" data-testid="academy-hero">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid md:grid-cols-2 gap-12 lg:gap-16 items-center">
        {/* Text Content */}
        <div className="space-y-8">
          <div 
            className="inline-block bg-white px-5 py-2"
            style={{ 
              border: '3px solid #1a1a1a',
              boxShadow: '4px 4px 0px #000',
              borderRadius: '0px'
            }}
          >
            <span className="font-poppins font-bold text-sm text-text-dark uppercase tracking-wide">Impact Boost Academy</span>
          </div>
          
          <h1 className="font-poppins font-black text-4xl md:text-5xl lg:text-6xl text-text-dark leading-[1.05]" data-testid="academy-title">
            Formación que se convierte en{' '}
            <span className="text-pink">acción</span>{' '}
            desde el día 1
          </h1>
          
          <p className="font-poppins text-lg md:text-xl text-text-dark/80 leading-relaxed">
            Cursos prácticos para profesionales y organizaciones sociales. 
            Aprende a escalar tu impacto con herramientas reales y una comunidad de apoyo.
          </p>

          {/* Dual CTAs - Yellow buttons stand out */}
          <div className="flex flex-wrap gap-4 pt-4">
            <a
              href="#ruta"
              className="inline-flex items-center gap-3 bg-yellow text-text-dark font-poppins font-bold uppercase tracking-widest px-8 py-4 border-4 border-text-dark hover:translate-y-1 transition-all"
              style={{ 
                boxShadow: '6px 6px 0px #000',
                borderRadius: '0px'
              }}
              data-testid="academy-hero-cta-calendar"
            >
              Ver calendario 2026
              <ArrowDown className="w-5 h-5" />
            </a>
            <a
              href="https://wa.me/593982525226?text=Hola%20Boost%20Change%2C%20quiero%20inscribir%20a%20mi%20equipo%20en%20Impact%20Boost%20Academy."
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-transparent text-text-dark font-poppins font-bold uppercase tracking-widest px-8 py-4 border-4 border-text-dark hover:bg-white transition-all"
              style={{ borderRadius: '0px' }}
              data-testid="academy-hero-cta-team"
            >
              Inscribir a mi equipo
            </a>
          </div>
        </div>

        {/* Visual - Clean rectangular frame */}
        <div className="relative">
          <div 
            className="w-full overflow-hidden bg-white"
            style={{ 
              border: '4px solid #1a1a1a',
              boxShadow: '8px 8px 0px #000',
              borderRadius: '0px'
            }}
          >
            <img
              src="https://customer-assets.emergentagent.com/job_project-status-62/artifacts/jlyftkr2_WhatsApp%20Image%202026-01-29%20at%204.39.03%20PM.jpeg"
              alt="Equipo en formación"
              className="w-full h-auto"
              style={{ display: 'block', borderRadius: '0px' }}
            />
          </div>
          
          {/* Stats badges - No rotation */}
          <div 
            className="absolute -top-4 -right-4 bg-pink px-4 py-2"
            style={{ 
              border: '3px solid #1a1a1a',
              boxShadow: '4px 4px 0px #000',
              borderRadius: '0px'
            }}
          >
            <span className="font-poppins font-bold text-sm text-white uppercase tracking-wide">NPS 9.7/10</span>
          </div>
          
          <div 
            className="absolute -bottom-4 -left-4 bg-lime px-4 py-2"
            style={{ 
              border: '3px solid #1a1a1a',
              boxShadow: '4px 4px 0px #000',
              borderRadius: '0px'
            }}
          >
            <span className="font-poppins font-bold text-sm text-text-dark uppercase tracking-wide">90% lo aplica el día 1</span>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// 4 Pillars Section - ¿Qué incluye la Academia?
const PillarsSection = () => {
  const pillars = [
    { 
      icon: <Clock className="w-10 h-10" strokeWidth={2.5} />, 
      title: "Formato Híbrido", 
      desc: "5 semanas de formación que combinan sesiones live (2h semanales) con contenido autoguiado de alto nivel.",
      iconBg: "bg-cyan"
    },
    { 
      icon: <Smartphone className="w-10 h-10" strokeWidth={2.5} />, 
      title: "Ecosistema Digital", 
      desc: "Acceso 24/7 desde cualquier dispositivo a nuestra plataforma web y App educativa.",
      iconBg: "bg-purple"
    },
    { 
      icon: <Award className="w-10 h-10" strokeWidth={2.5} />, 
      title: "Certificados Verificables", 
      desc: "Obtén un certificado digital con respaldo de Boost Change y reconocimiento verificable de tus logros.",
      iconBg: "bg-yellow"
    },
    { 
      icon: <Globe className="w-10 h-10" strokeWidth={2.5} />, 
      title: "Comunidad Virtual", 
      desc: "Networking estratégico con profesionales y líderes del sector en toda Latinoamérica.",
      iconBg: "bg-pink"
    },
  ];

  return (
    <section className="bg-cream py-16 md:py-24" data-testid="academy-pillars">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="font-poppins font-black text-3xl md:text-5xl text-text-dark text-center mb-4">
          ¿QUÉ INCLUYE LA ACADEMIA?
        </h2>
        <p className="font-poppins text-lg text-text-dark/70 text-center mb-12 max-w-2xl mx-auto">
          Todo lo que necesitas para una formación de alto nivel
        </p>
        
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {pillars.map((item, index) => (
            <div
              key={index}
              className="bg-white p-6"
              style={{ 
                border: '4px solid #1a1a1a',
                boxShadow: '8px 8px 0px #000',
                borderRadius: '0px'
              }}
              data-testid={`pillar-card-${index}`}
            >
              <div 
                className={`${item.iconBg} p-4 inline-block mb-5`}
                style={{ 
                  border: '3px solid #1a1a1a',
                  borderRadius: '0px'
                }}
              >
                <div className="text-text-dark">{item.icon}</div>
              </div>
              <h3 className="font-poppins font-bold text-lg text-text-dark mb-3">{item.title}</h3>
              <p className="font-poppins text-sm text-text-dark/70 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Learning Path Section - Ruta de Aprendizaje 2026
const LearningPathSection = () => {
  const courses = [
    { months: "ENE - FEB", title: "Tech-Social", subtitle: "IA y Tecnología", side: "left", color: "bg-cyan" },
    { months: "MAR - ABR", title: "Medir para Transformar", subtitle: "Sistemas MEL", side: "right", color: "bg-purple" },
    { months: "ABR - MAY", title: "Movilización de Recursos", subtitle: "Sostenibilidad Financiera", side: "left", color: "bg-pink" },
    { months: "MAY - JUN", title: "Power BI para Transformar", subtitle: "Visualización de datos", side: "right", color: "bg-blue" },
    { months: "JUL - AGO", title: "Re-Conéctate", subtitle: "Liderazgo - Presencial Ecuador", side: "left", color: "bg-lime" },
    { months: "AGO - SEP", title: "Design Thinking Social", subtitle: "Innovación ágil", side: "right", color: "bg-yellow" },
    { months: "SEP - OCT", title: "Tech-Social", subtitle: "2da cohorte", side: "left", color: "bg-cyan" },
    { months: "NOV - DIC", title: "Power BI para Transformar", subtitle: "2da cohorte", side: "right", color: "bg-blue" },
  ];

  return (
    <section id="ruta" className="bg-cream py-16 md:py-24" data-testid="academy-learning-path">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <h2 className="font-poppins font-black text-3xl md:text-5xl text-text-dark mb-6">
            TU RUTA DE APRENDIZAJE 2026
          </h2>
          <div 
            className="bg-white text-text-dark p-6 max-w-3xl mx-auto"
            style={{ 
              border: '4px solid #1a1a1a',
              boxShadow: '8px 8px 0px #000',
              borderRadius: '0px'
            }}
          >
            <p className="font-poppins text-base md:text-lg leading-relaxed">
              Nuestra ruta académica 2026 está diseñada en formato <strong className="text-pink">100% sincrónico y en vivo</strong>. 
              No son videos grabados; son espacios de conexión en tiempo real con expertos y pares 
              para acelerar tu aprendizaje y el de tu equipo.
            </p>
          </div>
        </div>
        
        {/* Vertical Timeline */}
        <div className="relative mt-16">
          {/* Central vertical line */}
          <div 
            className="absolute left-1/2 transform -translate-x-1/2 h-full bg-text-dark hidden md:block"
            style={{ width: '5px' }}
          ></div>
          
          {/* Mobile: simple line on left */}
          <div 
            className="absolute left-4 h-full bg-text-dark md:hidden"
            style={{ width: '4px' }}
          ></div>
          
          {/* Course cards */}
          <div className="space-y-8 md:space-y-12">
            {courses.map((course, index) => (
              <div 
                key={index} 
                className={`relative flex items-center ${course.side === 'left' ? 'md:flex-row' : 'md:flex-row-reverse'}`}
                data-testid={`timeline-course-${index}`}
              >
                {/* Timeline dot */}
                <div 
                  className="absolute left-4 md:left-1/2 transform md:-translate-x-1/2 w-5 h-5 bg-text-dark border-4 border-cream z-10"
                  style={{ borderRadius: '0px' }}
                ></div>
                
                {/* Card */}
                <div className={`ml-12 md:ml-0 md:w-5/12 ${course.side === 'left' ? 'md:pr-12' : 'md:pl-12'}`}>
                  <div 
                    className="bg-white p-5"
                    style={{ 
                      border: '4px solid #1a1a1a',
                      boxShadow: '6px 6px 0px #000',
                      borderRadius: '0px'
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <div 
                        className={`${course.color} px-3 py-1`}
                        style={{ border: '2px solid #1a1a1a', borderRadius: '0px' }}
                      >
                        <span className="font-poppins font-bold text-xs uppercase tracking-wide text-text-dark">{course.months}</span>
                      </div>
                    </div>
                    <h3 className="font-poppins font-bold text-lg text-text-dark mt-3">
                      {course.title}
                    </h3>
                    <p className="font-poppins text-sm text-text-dark/70">
                      {course.subtitle}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// UPDATED: Course Catalog Section - Cream background, white cards, colored icon boxes
const CourseCatalogSection = () => {
  const courses = [
    { 
      title: "Tech-Social", 
      desc: "IA aplicada, prompts efectivos y automatizaciones para eliminar tareas repetitivas.",
      icon: <Brain className="w-7 h-7" strokeWidth={2.5} />,
      iconBg: "bg-cyan",
      whatsappUrl: "https://wa.me/593982525226?text=Hola%20Boost%20Change%2C%20quiero%20saber%20m%C3%A1s%20sobre%20el%20curso%20Tech-Social.",
      hasLandingPage: true,
      landingPageUrl: "/academia/tech-social"
    },
    { 
      title: "Medir para Transformar", 
      desc: "Construye sistemas MEL participativos y orientados al impacto real.",
      icon: <Target className="w-7 h-7" strokeWidth={2.5} />,
      iconBg: "bg-purple",
      whatsappUrl: "https://wa.me/593982525226?text=Hola%20Boost%20Change%2C%20quiero%20saber%20m%C3%A1s%20sobre%20el%20curso%20Medir%20para%20Transformar.",
      hasLandingPage: false
    },
    { 
      title: "Movilización de Recursos", 
      desc: "Estrategias de fundraising y diversificación de ingresos para asegurar tu misión.",
      icon: <DollarSign className="w-7 h-7" strokeWidth={2.5} />,
      iconBg: "bg-pink",
      whatsappUrl: "https://wa.me/593982525226?text=Hola%20Boost%20Change%2C%20quiero%20saber%20m%C3%A1s%20sobre%20el%20curso%20Movilizaci%C3%B3n%20de%20Recursos.",
      hasLandingPage: true,
      landingPageUrl: "/academia/movilizacion-recursos"
    },
    { 
      title: "Power BI para Transformar", 
      desc: "Análisis, limpieza y visualización estratégica de datos de impacto.",
      icon: <BarChart3 className="w-7 h-7" strokeWidth={2.5} />,
      iconBg: "bg-blue",
      whatsappUrl: "https://wa.me/593982525226?text=Hola%20Boost%20Change%2C%20quiero%20saber%20m%C3%A1s%20sobre%20el%20curso%20Power%20BI%20para%20Transformar.",
      hasLandingPage: false
    },
    { 
      title: "Re-Conéctate", 
      desc: "Liderazgo transformador y prevención de burnout. Sesiones en vivo + salidas a la naturaleza en Ecuador.",
      icon: <Heart className="w-7 h-7" strokeWidth={2.5} />,
      iconBg: "bg-lime",
      whatsappUrl: "https://wa.me/593982525226?text=Hola%20Boost%20Change%2C%20quiero%20saber%20m%C3%A1s%20sobre%20el%20curso%20Re-Con%C3%A9ctate.",
      hasLandingPage: false
    },
    { 
      title: "Design Thinking Social", 
      desc: "Metodologías ágiles para diseñar proyectos centrados 100% en el usuario.",
      icon: <Lightbulb className="w-7 h-7" strokeWidth={2.5} />,
      iconBg: "bg-yellow",
      whatsappUrl: "https://wa.me/593982525226?text=Hola%20Boost%20Change%2C%20quiero%20saber%20m%C3%A1s%20sobre%20el%20curso%20Design%20Thinking%20Social.",
      hasLandingPage: false
    },
  ];

  return (
    <section id="cursos" className="bg-cream py-16 md:py-24" data-testid="academy-courses">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="font-poppins font-black text-3xl md:text-5xl text-text-dark mb-4">
            CATÁLOGO DE CURSOS
          </h2>
          <p className="font-poppins font-medium text-lg text-text-dark/70">
            6 programas diseñados para transformar organizaciones sociales
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {courses.map((course, index) => (
            <div
              key={index}
              className="bg-white p-6 flex flex-col"
              style={{ 
                border: '4px solid #1a1a1a',
                boxShadow: '8px 8px 0px #000',
                borderRadius: '0px'
              }}
              data-testid={`course-card-${index}`}
            >
              {/* Colored Icon Box - Small square in top left */}
              <div 
                className={`${course.iconBg} p-3 inline-block mb-5 self-start`}
                style={{ 
                  border: '3px solid #1a1a1a',
                  borderRadius: '0px'
                }}
              >
                <div className="text-text-dark">{course.icon}</div>
              </div>
              
              <h3 className="font-poppins font-bold text-xl text-text-dark mb-3">
                {course.title}
              </h3>
              <p className="font-poppins text-sm text-text-dark/70 leading-relaxed mb-6 flex-grow">
                {course.desc}
              </p>
              
              {/* Buttons - Different layout for courses with landing page */}
              {course.hasLandingPage ? (
                <div className="flex flex-col gap-2 self-start">
                  <Link
                    to={course.landingPageUrl}
                    className="inline-flex items-center gap-2 bg-lime text-text-dark font-poppins font-bold text-sm uppercase tracking-wider px-5 py-3 hover:translate-x-0.5 hover:translate-y-0.5 transition-all"
                    style={{ 
                      border: '2px solid #1a1a1a',
                      boxShadow: '3px 3px 0px #000',
                      borderRadius: '0px'
                    }}
                  >
                    VER CURSO
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                  <a
                    href={course.whatsappUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-white font-poppins font-bold text-sm uppercase tracking-wider px-5 py-3 hover:translate-x-0.5 hover:translate-y-0.5 transition-all"
                    style={{ 
                      backgroundColor: '#25D366',
                      border: '2px solid #1a1a1a',
                      boxShadow: '3px 3px 0px #000',
                      borderRadius: '0px'
                    }}
                  >
                    <WhatsAppIcon className="w-4 h-4" />
                    WHATSAPP
                  </a>
                </div>
              ) : (
                <a
                  href={course.whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-lime text-text-dark font-poppins font-bold text-sm uppercase tracking-wider px-5 py-3 hover:translate-y-1 transition-all self-start"
                  style={{ 
                    border: '3px solid #1a1a1a',
                    boxShadow: '4px 4px 0px #000',
                    borderRadius: '0px'
                  }}
                >
                  <WhatsAppIcon className="w-5 h-5" />
                  Más Info
                  <ArrowRight className="w-4 h-4" />
                </a>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// NEW: "Hablemos de tu formación" Section - Replaces Pricing
const ContactOptionsSection = () => (
  <section className="bg-cream py-16 md:py-24" data-testid="academy-contact-options">
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-12">
        <h2 className="font-poppins font-black text-3xl md:text-5xl text-text-dark mb-4">
          HABLEMOS DE TU FORMACIÓN
        </h2>
        <p className="font-poppins font-medium text-lg text-text-dark/70">
          Elige la opción que mejor se adapte a ti
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        {/* Card 1: Para Individuos */}
        <div
          className="bg-white p-8 flex flex-col"
          style={{ 
            border: '4px solid #1a1a1a',
            boxShadow: '8px 8px 0px #000',
            borderRadius: '0px'
          }}
          data-testid="contact-card-individual"
        >
          <div 
            className="bg-lime p-4 inline-block mb-6 self-start"
            style={{ 
              border: '3px solid #1a1a1a',
              borderRadius: '0px'
            }}
          >
            <User className="w-10 h-10 text-text-dark" strokeWidth={2.5} />
          </div>
          
          <h3 className="font-poppins font-black text-2xl text-text-dark mb-4">
            Profesional Independiente
          </h3>
          <p className="font-poppins text-text-dark/80 leading-relaxed mb-8 flex-grow">
            ¿Eres un profesional y buscas fortalecer tu perfil? Contáctanos para darte a conocer nuestros costos.
          </p>
          
          <a
            href="https://wa.me/593982525226?text=Hola%20Boost%20Change%2C%20soy%20un%20profesional%20independiente%20y%20busco%20fortalecer%20mi%20perfil.%20%C2%BFMe%20pueden%20dar%20m%C3%A1s%20informaci%C3%B3n%20sobre%20costos%20y%20planes%3F"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-lime text-text-dark font-poppins font-bold uppercase tracking-widest px-6 py-4 hover:translate-y-1 transition-all self-start"
            style={{ 
              border: '4px solid #1a1a1a',
              boxShadow: '6px 6px 0px #000',
              borderRadius: '0px'
            }}
          >
            <WhatsAppIcon className="w-5 h-5" />
            CHATEAR AHORA
            <ArrowRight className="w-5 h-5" />
          </a>
        </div>

        {/* Card 2: Para Organizaciones */}
        <div
          className="bg-white p-8 flex flex-col"
          style={{ 
            border: '4px solid #1a1a1a',
            boxShadow: '8px 8px 0px #000',
            borderRadius: '0px'
          }}
          data-testid="contact-card-organization"
        >
          <div 
            className="bg-blue p-4 inline-block mb-6 self-start"
            style={{ 
              border: '3px solid #1a1a1a',
              borderRadius: '0px'
            }}
          >
            <Users className="w-10 h-10 text-white" strokeWidth={2.5} />
          </div>
          
          <h3 className="font-poppins font-black text-2xl text-text-dark mb-4">
            Equipos y Organizaciones
          </h3>
          <p className="font-poppins text-text-dark/80 leading-relaxed mb-8 flex-grow">
            ¿Buscas fortalecer a tu organización? Tenemos planes de formación para tu organización que varían por número de personas y requerimientos de formación, conversemos.
          </p>
          
          <a
            href="https://wa.me/593982525226?text=Hola%20Boost%20Change%2C%20busco%20formaci%C3%B3n%20para%20mi%20organizaci%C3%B3n%2Fequipo.%20Me%20gustar%C3%ADa%20conversar%20sobre%20planes%20a%20medida."
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-blue text-white font-poppins font-bold uppercase tracking-widest px-6 py-4 hover:translate-y-1 transition-all self-start"
            style={{ 
              border: '4px solid #1a1a1a',
              boxShadow: '6px 6px 0px #000',
              borderRadius: '0px'
            }}
          >
            <Calendar className="w-5 h-5" />
            AGENDAR LLAMADA
            <ArrowRight className="w-5 h-5" />
          </a>
        </div>
      </div>
    </div>
  </section>
);

// CTA Section
const CTASection = () => (
  <section className="bg-blue py-16" data-testid="academy-cta-section">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <div 
        className="bg-white p-10"
        style={{ 
          border: '4px solid #1a1a1a',
          boxShadow: '8px 8px 0px #000',
          borderRadius: '0px'
        }}
      >
        <h2 className="font-poppins font-black text-3xl md:text-4xl text-text-dark mb-6">
          ¿Listo para transformar a tu equipo?
        </h2>
        <p className="font-poppins text-lg text-text-dark/70 mb-8 max-w-2xl mx-auto">
          Escríbenos y te ayudamos a encontrar la formación perfecta para tu organización.
        </p>
        <a
          href="https://wa.me/593982525226?text=Hola%20Boost%20Change%2C%20quiero%20informaci%C3%B3n%20sobre%20Impact%20Boost%20Academy."
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-3 bg-yellow text-text-dark font-poppins font-bold uppercase tracking-widest px-8 py-4 hover:translate-y-1 transition-all"
          style={{ 
            border: '4px solid #1a1a1a',
            boxShadow: '6px 6px 0px #000',
            borderRadius: '0px'
          }}
          data-testid="academy-final-cta"
        >
          <WhatsAppIcon className="w-5 h-5" />
          ESCRÍBENOS
          <ArrowRight className="w-5 h-5" />
        </a>
      </div>
    </div>
  </section>
);

// Footer
const Footer = () => (
  <footer className="bg-text-dark py-12">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col md:flex-row justify-between items-center gap-6">
        <Link to="/" className="font-poppins font-black text-2xl text-white">
          BOOST<span className="text-pink">CHANGE</span>
        </Link>
        <div className="flex gap-6">
          <Link to="/" className="font-poppins text-white/80 hover:text-pink transition-colors">Inicio</Link>
          <Link to="/sistemas-de-impacto" className="font-poppins text-white/80 hover:text-pink transition-colors">Sistemas</Link>
          <Link to="/boost-ops" className="font-poppins text-white/80 hover:text-pink transition-colors">Boost Ops</Link>
        </div>
        <p className="font-poppins text-white/60 text-sm">2024 Boost Change</p>
      </div>
    </div>
  </footer>
);

// Main Page Component - UPDATED structure
const ImpactBoostAcademyPage = () => {
  return (
    <div className="min-h-screen bg-cream" data-testid="academy-page">
      <Navigation />
      <main>
        <HeroSection />
        <PillarsSection />
        <LearningPathSection />
        <CourseCatalogSection />
        <ContactOptionsSection />
        <CTASection />
      </main>
      <Footer />
    </div>
  );
};

export default ImpactBoostAcademyPage;
